import React from 'react';
import { Users, Wifi, AlertTriangle, TrendingUp, TrendingDown, Clock, Calendar, Activity, Shield } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import './OverviewWidgets.css';

const OverviewWidgets = ({ users, onWidgetClick }) => {
  // Calculate metrics from users data
  const totalUsers = 22; // Fixed total users count
  const onlineUsers = users.filter(user => user.pillboxStatus === 'Online').length;
  const offlineUsers = users.filter(user => user.pillboxStatus === 'Offline').length;
  const notAssignedUsers = users.filter(user => user.pillboxStatus === 'Not Assigned').length;
  
  // Calculate adherence metrics
  const averageAdherence = users.reduce((sum, user) => {
    return sum + parseFloat(user.overallAdherence.replace('%', ''));
  }, 0) / totalUsers;
  
  const highAdherenceUsers = users.filter(user => 
    parseFloat(user.overallAdherence.replace('%', '')) >= 75
  ).length;
  
  const lowAdherenceUsers = users.filter(user => 
    parseFloat(user.overallAdherence.replace('%', '')) < 50
  ).length;
  
  // Calculate recent activity (mock data based on current setup)
  const recentActivity = Math.floor(Math.random() * 24) + 1; // Hours since last activity
  const totalDoses = users.reduce((sum, user) => {
    const doses = user.dosingOpportunities.split('/')[0].trim();
    return sum + parseInt(doses);
  }, 0);
  
  const completedDoses = Math.floor(totalDoses * (averageAdherence / 100));
  const missedDoses = totalDoses - completedDoses;

  const widgets = [
    {
      id: 'total-users',
      title: 'Total Users',
      value: totalUsers,
      subtitle: 'Active patients',
      icon: Users,
      color: '#3b82f6',
      bgColor: '#eff6ff',
      change: '+2 this week',
      route: '/analytics/users'
    },
    {
      id: 'online-status',
      title: 'Device Status',
      value: onlineUsers,
      subtitle: `${onlineUsers} online, ${offlineUsers} offline`,
      icon: Wifi,
      color: '#16a34a',
      bgColor: '#f0fdf4',
      change: `${notAssignedUsers} not assigned`,
      route: '/analytics/devices'
    },
    {
      id: 'adherence-overview',
      title: 'Average Adherence',
      value: `${averageAdherence.toFixed(1)}%`,
      subtitle: `${highAdherenceUsers} high, ${lowAdherenceUsers} low`,
      icon: TrendingUp,
      color: averageAdherence >= 75 ? '#16a34a' : averageAdherence >= 50 ? '#f59e0b' : '#dc2626',
      bgColor: averageAdherence >= 75 ? '#f0fdf4' : averageAdherence >= 50 ? '#fffbeb' : '#fef2f2',
      change: 'Last 7 days',
      route: '/analytics/adherence'
    },
    {
      id: 'alerts',
      title: 'Active Alerts',
      value: lowAdherenceUsers + Math.floor(Math.random() * 3),
      subtitle: 'Requires attention',
      icon: AlertTriangle,
      color: '#dc2626',
      bgColor: '#fef2f2',
      change: 'View details',
      route: '/analytics/alerts'
    },
    {
      id: 'doses-today',
      title: 'Doses Today',
      value: completedDoses,
      subtitle: `${missedDoses} missed, ${totalDoses} total`,
      icon: Activity,
      color: '#7c3aed',
      bgColor: '#faf5ff',
      change: 'Current day',
      route: '/analytics/doses'
    },
    {
      id: 'recent-activity',
      title: 'Recent Activity',
      value: `${recentActivity}h`,
      subtitle: 'Last patient interaction',
      icon: Clock,
      color: '#0891b2',
      bgColor: '#f0f9ff',
      change: 'Patient ID: 47',
      route: '/analytics/activity'
    },
    {
      id: 'avg-treatment',
      title: 'Avg. Treatment Days',
      value: Math.floor(users.reduce((sum, user) => sum + user.daysInTreatment, 0) / totalUsers),
      subtitle: 'Days in program',
      icon: Calendar,
      color: '#ea580c',
      bgColor: '#fff7ed',
      change: 'All patients',
      route: '/analytics/treatment'
    },
    {
      id: 'compliance-rate',
      title: 'Weekly Compliance',
      value: `${(averageAdherence + Math.random() * 10 - 5).toFixed(1)}%`,
      subtitle: 'This week vs last',
      icon: TrendingUp,
      color: '#16a34a',
      bgColor: '#f0fdf4',
      change: '+5.2% from last week',
      route: '/analytics/compliance'
    },
    {
      id: 'critical-patients',
      title: 'Critical Patients',
      value: Math.max(0, users.filter(user => parseFloat(user.overallAdherence.replace('%', '')) < 25).length),
      subtitle: 'Immediate attention needed',
      icon: Shield,
      color: '#dc2626',
      bgColor: '#fef2f2',
      change: 'Requires intervention',
      route: '/analytics/critical'
    }
  ];

  const handleWidgetClick = (widgetType, widgetData) => {
    if (onWidgetClick) {
      onWidgetClick(widgetType, widgetData);
    }
  };

  // Calculate Overall Adherence from actual patient data
  const calculateOverallAdherence = () => {
    const totalAdherence = users.reduce((sum, user) => {
      return sum + parseFloat(user.overallAdherence.replace('%', ''));
    }, 0);
    const avgAdherence = totalAdherence / totalUsers;
    return Math.round(avgAdherence);
  };

  const overallAdherencePercent = calculateOverallAdherence();
  const missedPercent = 100 - overallAdherencePercent;

  // Data for Overall Adherence Chart - custom colors
  const adherenceData = [
    { name: 'Taken', value: overallAdherencePercent, color: '#3b82f6' }, // Blue
    { name: 'Missed', value: missedPercent, color: '#FBB295' } // Orange/Peach
  ];

  // Data for Dose Administration Time Distribution
  const doseTimeData = [
    { time: -120, doses: 8 },
    { time: -90, doses: 7 },
    { time: -60, doses: 6 },
    { time: -30, doses: 10 },
    { time: -15, doses: 8 },
    { time: 0, doses: 50 },
    { time: 15, doses: 30 },
    { time: 30, doses: 50 },
    { time: 60, doses: 20 },
    { time: 90, doses: 8 },
    { time: 120, doses: 5 }
  ];

  return (
    <div className="overview-widgets">
      {/* Overview Charts Section */}
      <div className="overview-charts-section">
        {/* Overall Adherence Chart */}
        <div className="overview-chart-card">
          <h3 className="chart-title">Overall Adherence</h3>
          <div className="chart-content">
            <div className="donut-chart-container">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={adherenceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {adherenceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="donut-center-text">
                <span className="donut-percentage">{overallAdherencePercent}<span className="percent-sign">%</span></span>
              </div>
            </div>
            <div className="chart-legend-horizontal">
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#3b82f6' }}></div>
                <span>Taken</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#FBB295' }}></div>
                <span>Missed</span>
              </div>
            </div>
            <div className="box-plot-container">
              <svg width="100%" height="100" viewBox="0 0 720 100" preserveAspectRatio="xMidYMid meet">
            {/* Taken box plot (blue) */}
            <g>
              {/* Whisker line - min to max */}
              <line x1="50" y1="20" x2="620" y2="20" stroke="#3b82f6" strokeWidth="2" />
              {/* Min whisker cap */}
              <line x1="50" y1="12" x2="50" y2="28" stroke="#3b82f6" strokeWidth="2" />
              {/* Max whisker cap */}
              <line x1="620" y1="12" x2="620" y2="28" stroke="#3b82f6" strokeWidth="2" />
              {/* Box - Q1 to Q3 */}
              <rect x="150" y="10" width="280" height="20" fill="#3b82f6" opacity="0.8" stroke="#3b82f6" strokeWidth="2" />
              {/* Median line */}
              <line x1="290" y1="10" x2="290" y2="30" stroke="#3b82f6" strokeWidth="3" />
              {/* Outlier dots */}
              <circle cx="30" cy="20" r="3" fill="#3b82f6" />
              <circle cx="100" cy="20" r="3" fill="#3b82f6" />
              <circle cx="180" cy="20" r="3" fill="#3b82f6" />
              <circle cx="350" cy="20" r="3" fill="#3b82f6" />
              <circle cx="480" cy="20" r="3" fill="#3b82f6" />
              <circle cx="580" cy="20" r="3" fill="#3b82f6" />
              <circle cx="680" cy="20" r="3" fill="#3b82f6" />
            </g>
                
                {/* Missed box plot (orange/peach) */}
                <g>
                  {/* Whisker line - min to max */}
                  <line x1="170" y1="55" x2="620" y2="55" stroke="#FBB295" strokeWidth="2" />
                  {/* Min whisker cap */}
                  <line x1="170" y1="47" x2="170" y2="63" stroke="#FBB295" strokeWidth="2" />
                  {/* Max whisker cap */}
                  <line x1="620" y1="47" x2="620" y2="63" stroke="#FBB295" strokeWidth="2" />
                  {/* Box - Q1 to Q3 */}
                  <rect x="280" y="45" width="210" height="20" fill="#FBB295" opacity="0.8" stroke="#FBB295" strokeWidth="2" />
                  {/* Median line */}
                  <line x1="385" y1="45" x2="385" y2="65" stroke="#FBB295" strokeWidth="3" />
                  {/* Outlier dots */}
                  <circle cx="680" cy="55" r="3" fill="#FBB295" />
                  <circle cx="700" cy="55" r="3" fill="#FBB295" />
                </g>
                
                {/* Percentage labels */}
                <text x="0" y="90" fontSize="12" fill="#6b7280" textAnchor="start">0</text>
                <text x="144" y="90" fontSize="12" fill="#6b7280" textAnchor="middle">20</text>
                <text x="288" y="90" fontSize="12" fill="#6b7280" textAnchor="middle">40</text>
                <text x="432" y="90" fontSize="12" fill="#6b7280" textAnchor="middle">60</text>
                <text x="576" y="90" fontSize="12" fill="#6b7280" textAnchor="middle">80</text>
                <text x="720" y="90" fontSize="12" fill="#6b7280" textAnchor="end">100%</text>
              </svg>
            </div>
          </div>
        </div>

        {/* Dose Administration Time Chart */}
        <div className="overview-chart-card">
          <h3 className="chart-title">Dose Administration Time</h3>
          <div className="chart-content">
            <div className="distribution-chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={doseTimeData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="time"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#d1d5db' }}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '12px'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="doses"
                    stroke="#0891b2"
                    fill="#0891b2"
                    fillOpacity={0.5}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-legend-horizontal">
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#0891b2' }}></div>
                <span>All Patients</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      
      <div className="widgets-grid">
        {widgets.map((widget) => {
          const IconComponent = widget.icon;
          return (
            <div 
              key={widget.id} 
              className="widget-card clickable"
              onClick={() => handleWidgetClick(widget.id, { 
                title: widget.title,
                users: users,
                widgetData: widget 
              })}
            >
              <div className="widget-header">
                <div 
                  className="widget-icon"
                  style={{ 
                    backgroundColor: widget.bgColor,
                    color: widget.color 
                  }}
                >
                  <IconComponent size={24} />
                </div>
                <div className="widget-info">
                  <h3>{widget.title}</h3>
                  <div className="widget-value" style={{ color: widget.color }}>
                    {widget.value}
                  </div>
                </div>
              </div>
              
              <div className="widget-details">
                <p className="widget-subtitle">{widget.subtitle}</p>
                <span className={`widget-change ${widget.change.includes('+') ? 'positive' : widget.change.includes('-') ? 'negative' : 'neutral'}`}>
                  {widget.change.includes('+') && <TrendingUp size={12} />}
                  {widget.change.includes('-') && <TrendingDown size={12} />}
                  {widget.change}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverviewWidgets;
